/**
 * CEFR Knowledge Tree — Organic Tree Visualization
 *
 * A procedurally-generated tree where:
 *   - Trunk grows from the ground, curves and tapers naturally
 *   - Branches fork recursively with bezier curves
 *   - CEFR levels map to vertical bands (A1 base → C2 crown)
 *   - Knowledge nodes sit as "fruit" on branches
 *   - Category colors distinguish grammar / vocabulary / usage
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { fetchGraphTree, type GraphTreeNode } from '../../services/knowledgeGraphService';
import './CEFRTreePage.css';

// ── Constants ────────────────────────────────────────────────────
const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

const CEFR_COLORS: Record<string, string> = {
  A1: '#22c55e', A2: '#4ade80',
  B1: '#eab308', B2: '#f97316',
  C1: '#ef4444', C2: '#dc2626',
};

const CATEGORY_COLORS: Record<string, string> = {
  grammar: '#7da8e0',
  vocabulary: '#6bc99d',
  usage: '#b48ee0',
};

const CATEGORY_COLORS_DIM: Record<string, string> = {
  grammar: 'rgba(125,168,224,0.35)',
  vocabulary: 'rgba(107,201,157,0.35)',
  usage: 'rgba(180,142,224,0.35)',
};

// ── Data types ───────────────────────────────────────────────────
interface FlatNode {
  id: string;
  code: string;
  label: string;
  labelZh: string;
  category: 'grammar' | 'vocabulary' | 'usage';
  cefrMin: string;
  cefrMax: string;
  kpCount: number;
  parentCode: string;
  depth: number;
  isParent: boolean;
  childCount: number;
}

function flattenTree(nodes: GraphTreeNode[], parentCode = '', depth = 0): FlatNode[] {
  const result: FlatNode[] = [];
  for (const node of nodes) {
    const hasChildren = node.children && node.children.length > 0;
    result.push({
      id: node.id,
      code: node.code,
      label: node.labels?.en || node.code,
      labelZh: node.labels?.['zh-TW'] || '',
      category: node.category,
      cefrMin: node.cefrLevelMin,
      cefrMax: node.cefrLevelMax,
      kpCount: node.knowledgePointCount || 0,
      parentCode,
      depth,
      isParent: !!hasChildren,
      childCount: hasChildren ? node.children!.length : 0,
    });
    if (hasChildren) {
      result.push(...flattenTree(node.children!, node.code, depth + 1));
    }
  }
  return result;
}

// ── Seeded pseudo-random ─────────────────────────────────────────
function seededRand(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  return ((h & 0x7fffffff) % 10000) / 10000;
}

// ── Organic tree generation ──────────────────────────────────────
interface BranchPath {
  d: string;           // SVG path data
  thickness: number;
  opacity: number;
  isRoot?: boolean;
}

interface NodePlacement {
  x: number;
  y: number;
  r: number;
  node: FlatNode;
  stemX: number;       // branch attachment point
  stemY: number;
}

interface TreeGeometry {
  paths: BranchPath[];
  nodes: NodePlacement[];
  canopyPoints: { x: number; y: number }[];
}

function buildOrganicTree(
  flatNodes: FlatNode[],
  w: number,
  h: number,
  filter: string | null,
): TreeGeometry {
  const cx = w / 2;
  const ground = h - 40;
  const crown = 55;
  const totalH = ground - crown;

  // Band geometry
  const bandH = totalH / 6;
  const bandBottom = (i: number) => ground - bandH * i;
  const bandMid = (i: number) => ground - bandH * i - bandH * 0.5;

  const paths: BranchPath[] = [];
  const nodes: NodePlacement[] = [];
  const canopyPoints: { x: number; y: number }[] = [];

  // ── TRUNK ──
  // Grows from ground, slight S-curve, splits at ~38% height
  const trunkSplitY = ground - totalH * 0.38;
  const trunkLean = seededRand('trunk-lean') * 12 - 6;

  // Wide base roots
  const rootSpread = w * 0.06;
  paths.push({
    d: `M ${cx - rootSpread} ${ground + 4} Q ${cx - rootSpread * 0.5} ${ground - 8} ${cx - 2} ${ground - 20}`,
    thickness: 5, opacity: 0.7,
  });
  paths.push({
    d: `M ${cx + rootSpread} ${ground + 4} Q ${cx + rootSpread * 0.5} ${ground - 8} ${cx + 2} ${ground - 20}`,
    thickness: 5, opacity: 0.7,
  });
  paths.push({
    d: `M ${cx - rootSpread * 1.6} ${ground + 8} Q ${cx - rootSpread * 0.9} ${ground - 2} ${cx - 4} ${ground - 14}`,
    thickness: 3, opacity: 0.5,
  });
  paths.push({
    d: `M ${cx + rootSpread * 1.6} ${ground + 8} Q ${cx + rootSpread * 0.9} ${ground - 2} ${cx + 4} ${ground - 14}`,
    thickness: 3, opacity: 0.5,
  });

  // Main trunk
  const trunkMidY = (ground + trunkSplitY) / 2;
  paths.push({
    d: `M ${cx} ${ground - 10} C ${cx + trunkLean * 0.3} ${trunkMidY + totalH * 0.08} ${cx + trunkLean} ${trunkMidY - totalH * 0.05} ${cx + trunkLean * 0.5} ${trunkSplitY}`,
    thickness: 14, opacity: 1, isRoot: true,
  });

  // Trunk continuation upward (thinner)
  const trunkTopY = ground - totalH * 0.65;
  paths.push({
    d: `M ${cx + trunkLean * 0.5} ${trunkSplitY} Q ${cx + trunkLean * 0.3} ${(trunkSplitY + trunkTopY) / 2} ${cx + trunkLean * 0.1} ${trunkTopY}`,
    thickness: 7, opacity: 0.9, isRoot: true,
  });

  // ── BRANCH GENERATION ──
  // Tree silhouette: widest at B1-B2 (mid canopy), narrowing at A1 (low) and C2 (crown)
  // This creates the natural dome/oval canopy shape
  const canopyWidth = (cefrIdx: number): number => {
    // Bell curve: widest at index 2-3 (B1-B2), narrower at edges
    const peak = 2.8;
    const spread = 2.2;
    const normalized = Math.exp(-0.5 * Math.pow((cefrIdx - peak) / spread, 2));
    return w * 0.18 + w * 0.26 * normalized;
  };

  // For each CEFR band, generate branches from the trunk/main limbs
  interface BranchTip {
    x: number;
    y: number;
    angle: number;
    thickness: number;
  }

  const allTips: { tip: BranchTip; cefrIdx: number }[] = [];

  for (let bi = 0; bi < 6; bi++) {
    const level = CEFR_LEVELS[bi];
    const bBot = bandBottom(bi);
    const bMid = bandMid(bi);
    const maxSpread = canopyWidth(bi);

    // How many main branches at this level
    const branchCount = bi < 2 ? 4 : bi < 4 ? 5 : 3;

    // Trunk attachment point at this height
    const trunkX = cx + trunkLean * (0.5 - bi * 0.08);
    const trunkY = bMid + bandH * 0.1;

    for (let br = 0; br < branchCount; br++) {
      // Strictly alternate left (-1) and right (+1)
      const side = br % 2 === 0 ? -1 : 1;
      const brSeed = `${level}-${br}`;
      const r1 = seededRand(brSeed + 'a');
      const r2 = seededRand(brSeed + 'b');
      const r3 = seededRand(brSeed + 'c');

      // Branch length
      const branchLength = maxSpread * (0.4 + r2 * 0.35);

      // Start on the trunk, slightly offset
      const startX = trunkX + side * (2 + r3 * 6);
      const startY = bBot - bandH * (0.15 + r1 * 0.55);

      // End point: go outward (side direction) and slightly upward
      // Upward component increases for higher CEFR levels
      const upwardRatio = 0.25 + bi * 0.06;
      const outwardJitter = (r1 - 0.5) * 0.15;
      const endX = startX + side * branchLength * (0.85 + outwardJitter);
      const endY = startY - branchLength * (upwardRatio + r2 * 0.15);

      // Control point: curve slightly upward, bowing away from trunk
      const ctrlX = (startX + endX) / 2 + side * branchLength * 0.08 * (r3 - 0.3);
      const ctrlY = Math.min(startY, endY) - branchLength * (0.08 + r3 * 0.12);

      const thickness = Math.max(1.8, 6 - bi * 0.5 - br * 0.3);

      paths.push({
        d: `M ${startX} ${startY} Q ${ctrlX} ${ctrlY} ${endX} ${endY}`,
        thickness,
        opacity: 0.7 + r1 * 0.3,
      });

      // Generate 2-3 sub-branches (twigs) forking from this branch
      const twigCount = 2 + (r3 > 0.5 ? 1 : 0);
      for (let tw = 0; tw < twigCount; tw++) {
        const t = (tw + 0.5) / twigCount;
        // Point along the quadratic bezier at parameter t
        const twBaseX = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * ctrlX + t * t * endX;
        const twBaseY = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * ctrlY + t * t * endY;

        const twSeed = `${brSeed}-tw${tw}`;
        const tr1 = seededRand(twSeed + 'x');
        const tr2 = seededRand(twSeed + 'y');

        // Twigs continue outward in the same direction with some spread
        const twLen = branchLength * (0.18 + tr2 * 0.22);
        const twSpread = (tr1 - 0.5) * 0.6; // angular spread
        const twEndX = twBaseX + side * twLen * (0.6 + Math.abs(twSpread));
        const twEndY = twBaseY - twLen * (0.4 + tr2 * 0.3) + twSpread * twLen * 0.3;

        const twCtrlX = (twBaseX + twEndX) / 2;
        const twCtrlY = twEndY - twLen * 0.06;

        paths.push({
          d: `M ${twBaseX} ${twBaseY} Q ${twCtrlX} ${twCtrlY} ${twEndX} ${twEndY}`,
          thickness: Math.max(1, thickness * 0.4),
          opacity: 0.5 + tr1 * 0.3,
        });

        allTips.push({
          tip: { x: twEndX, y: twEndY, angle: 0, thickness: thickness * 0.4 },
          cefrIdx: bi,
        });
      }

      // The main branch endpoint is also a tip
      allTips.push({
        tip: { x: endX, y: endY, angle: 0, thickness },
        cefrIdx: bi,
      });
      canopyPoints.push({ x: endX, y: endY });
    }
  }

  // ── PLACE NODES ON BRANCHES ──
  const filteredNodes = filter
    ? flatNodes.filter(n => n.category === filter)
    : flatNodes;

  // Group by CEFR min level
  const grouped = new Map<number, FlatNode[]>();
  for (let i = 0; i < 6; i++) grouped.set(i, []);

  for (const fn of filteredNodes) {
    const idx = CEFR_LEVELS.indexOf(fn.cefrMin as typeof CEFR_LEVELS[number]);
    const i = idx >= 0 ? idx : 0;
    grouped.get(i)!.push(fn);
  }

  // For each CEFR band, distribute nodes around branch tips
  for (let bi = 0; bi < 6; bi++) {
    const bandNodes = grouped.get(bi) || [];
    if (bandNodes.length === 0) continue;

    const tips = allTips.filter(t => t.cefrIdx === bi);
    const maxSpread = canopyWidth(bi);
    const bBot = bandBottom(bi);

    for (let ni = 0; ni < bandNodes.length; ni++) {
      const fn = bandNodes[ni];
      const r1 = seededRand(fn.code + 'nx');
      const r2 = seededRand(fn.code + 'ny');
      const r3 = seededRand(fn.code + 'ns');

      // Pick nearest tip or distribute evenly
      const tipIdx = ni % tips.length;
      const tip = tips[tipIdx]?.tip;

      let x: number, y: number, stemX: number, stemY: number;

      if (tip) {
        // Place near the branch tip with some jitter
        const jitterX = (r1 - 0.5) * 40;
        const jitterY = (r2 - 0.5) * 30;
        x = tip.x + jitterX;
        y = tip.y + jitterY - 5;
        stemX = tip.x;
        stemY = tip.y;
      } else {
        // Fallback: scatter in the band
        const side = ni % 2 === 0 ? -1 : 1;
        x = cx + side * maxSpread * (0.2 + r1 * 0.6);
        y = bBot - bandH * (0.1 + r2 * 0.8);
        stemX = x;
        stemY = y;
      }

      // Node radius
      const r = fn.isParent
        ? Math.max(6, Math.min(14, 5 + fn.childCount * 1.2))
        : Math.max(3, Math.min(10, fn.kpCount > 0 ? Math.log2(fn.kpCount + 1) * 2 : 2));

      nodes.push({ x, y, r, node: fn, stemX, stemY });
    }
  }

  return { paths, nodes, canopyPoints };
}

// ── Component ────────────────────────────────────────────────────
const CEFRTreePage: React.FC = () => {
  const [tree, setTree] = useState<GraphTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; node: FlatNode } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 1400, h: 900 });

  useEffect(() => {
    fetchGraphTree()
      .then(setTree)
      .catch(err => console.error('Failed to load tree:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setDims({ w: containerRef.current.clientWidth, h: containerRef.current.clientHeight });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const flatNodes = useMemo(() => flattenTree(tree), [tree]);
  const geometry = useMemo(
    () => buildOrganicTree(flatNodes, dims.w, dims.h, categoryFilter),
    [flatNodes, dims.w, dims.h, categoryFilter],
  );

  const handleNodeEnter = useCallback((e: React.MouseEvent, node: FlatNode) => {
    setTooltip({ x: e.clientX + 14, y: e.clientY - 12, node });
  }, []);
  const handleNodeMove = useCallback((e: React.MouseEvent) => {
    setTooltip(prev => prev ? { ...prev, x: e.clientX + 14, y: e.clientY - 12 } : null);
  }, []);
  const handleNodeLeave = useCallback(() => setTooltip(null), []);

  const bandH = (dims.h - 95) / 6;
  const parentCount = flatNodes.filter(n => n.isParent).length;
  const leafCount = flatNodes.filter(n => !n.isParent).length;

  if (loading) {
    return (
      <div className="cefr-tree-page">
        <div className="cefr-tree-loading">
          <div className="spinner" />
          <span>Loading knowledge graph...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="cefr-tree-page">
      {/* Header */}
      <header className="cefr-tree-header">
        <h1>Knowledge Tree</h1>
        <div className="header-controls">
          <button
            className={categoryFilter === null ? 'active' : ''}
            onClick={() => setCategoryFilter(null)}
          >
            All
          </button>
          {(['grammar', 'vocabulary', 'usage'] as const).map(cat => (
            <button
              key={cat}
              className={categoryFilter === cat ? 'active' : ''}
              onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
              style={categoryFilter === cat ? {
                borderColor: CATEGORY_COLORS[cat],
                color: CATEGORY_COLORS[cat],
                background: `${CATEGORY_COLORS[cat]}18`,
              } : {}}
            >
              {cat === 'grammar' ? '文法' : cat === 'vocabulary' ? '詞彙' : '用法'}
            </button>
          ))}
        </div>
      </header>

      {/* SVG Canvas */}
      <div className="cefr-tree-canvas" ref={containerRef}>
        <svg viewBox={`0 0 ${dims.w} ${dims.h}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            {/* Sky gradient */}
            <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0c1929" />
              <stop offset="60%" stopColor="#0f1f33" />
              <stop offset="100%" stopColor="#13241a" />
            </linearGradient>
            {/* Ground gradient */}
            <radialGradient id="ground" cx="50%" cy="0%" r="80%">
              <stop offset="0%" stopColor="#1a2e1a" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#0f1f33" stopOpacity="0" />
            </radialGradient>
            {/* Trunk fill */}
            <linearGradient id="bark" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#5c3d2e" />
              <stop offset="50%" stopColor="#4a3228" />
              <stop offset="100%" stopColor="#3d2a20" />
            </linearGradient>
            {/* Branch color */}
            <linearGradient id="branch-color" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#4a3228" />
              <stop offset="100%" stopColor="#3d2a20" stopOpacity="0.5" />
            </linearGradient>
            {/* Canopy glow */}
            <radialGradient id="canopy-glow" cx="50%" cy="45%" r="45%">
              <stop offset="0%" stopColor="#1a3d1a" stopOpacity="0.15" />
              <stop offset="60%" stopColor="#1a3d1a" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#1a3d1a" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Background */}
          <rect width={dims.w} height={dims.h} fill="url(#sky)" />

          {/* Canopy ambient glow */}
          <ellipse
            cx={dims.w / 2} cy={dims.h * 0.4}
            rx={dims.w * 0.38} ry={dims.h * 0.35}
            fill="url(#canopy-glow)"
          />

          {/* CEFR band labels + divider lines */}
          {CEFR_LEVELS.map((level, i) => {
            const bandTop = dims.h - 40 - bandH * (i + 1);
            const bandCenter = dims.h - 40 - bandH * i - bandH * 0.5;
            return (
              <g key={`band-${level}`}>
                {/* Divider line at top of each band */}
                {i > 0 && (
                  <line
                    x1={30} y1={bandTop + bandH} x2={dims.w - 20} y2={bandTop + bandH}
                    stroke={CEFR_COLORS[CEFR_LEVELS[i - 1]]}
                    strokeOpacity={0.08}
                    strokeWidth={0.5}
                    strokeDasharray="4 12"
                  />
                )}
                {/* CEFR label — right edge */}
                <text
                  x={dims.w - 18}
                  y={bandCenter}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill={CEFR_COLORS[level]}
                  fillOpacity={0.55}
                  fontSize={13}
                  fontWeight={700}
                  letterSpacing="0.04em"
                  style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                >
                  {level}
                </text>
                {/* CEFR label — left edge */}
                <text
                  x={18}
                  y={bandCenter}
                  textAnchor="start"
                  dominantBaseline="middle"
                  fill={CEFR_COLORS[level]}
                  fillOpacity={0.55}
                  fontSize={13}
                  fontWeight={700}
                  letterSpacing="0.04em"
                  style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                >
                  {level}
                </text>
              </g>
            );
          })}

          {/* Ground */}
          <ellipse
            cx={dims.w / 2} cy={dims.h - 28}
            rx={dims.w * 0.3} ry={18}
            fill="url(#ground)"
          />

          {/* Branches — organic curved paths */}
          {geometry.paths.map((p, i) => (
            <path
              key={`branch-${i}`}
              d={p.d}
              fill="none"
              stroke={p.isRoot ? 'url(#bark)' : '#4a3228'}
              strokeWidth={p.thickness}
              strokeOpacity={p.opacity}
              strokeLinecap="round"
            />
          ))}

          {/* Node stems — thin lines connecting nodes to branch tips */}
          {geometry.nodes.map((n, i) => {
            const dist = Math.hypot(n.x - n.stemX, n.y - n.stemY);
            if (dist < 3) return null;
            return (
              <line
                key={`stem-${i}`}
                x1={n.stemX} y1={n.stemY}
                x2={n.x} y2={n.y}
                stroke="#3d2a20"
                strokeWidth={0.8}
                strokeOpacity={0.4}
              />
            );
          })}

          {/* Parent nodes (hollow, dashed) */}
          {geometry.nodes.filter(n => n.node.isParent).map((n, i) => {
            const color = CATEGORY_COLORS[n.node.category] || '#94a3b8';
            return (
              <g
                key={`p-${n.node.id || i}`}
                onMouseEnter={(e) => handleNodeEnter(e, n.node)}
                onMouseMove={handleNodeMove}
                onMouseLeave={handleNodeLeave}
                style={{ cursor: 'pointer' }}
              >
                <circle
                  cx={n.x} cy={n.y} r={n.r}
                  fill={CATEGORY_COLORS_DIM[n.node.category] || 'rgba(148,163,184,0.2)'}
                  stroke={color}
                  strokeWidth={1.5}
                  strokeDasharray="3 2"
                  strokeOpacity={0.6}
                />
                <text
                  x={n.x} y={n.y + 1}
                  textAnchor="middle" dominantBaseline="middle"
                  fill={color} fontSize={Math.max(7, n.r * 0.65)}
                  fontWeight={700} style={{ pointerEvents: 'none' }}
                >
                  {n.node.childCount}
                </text>
              </g>
            );
          })}

          {/* Leaf nodes (filled) */}
          {geometry.nodes.filter(n => !n.node.isParent).map((n, i) => {
            const color = CATEGORY_COLORS[n.node.category] || '#94a3b8';
            return (
              <g
                key={`l-${n.node.id || i}`}
                onMouseEnter={(e) => handleNodeEnter(e, n.node)}
                onMouseMove={handleNodeMove}
                onMouseLeave={handleNodeLeave}
                style={{ cursor: 'pointer' }}
              >
                <circle
                  cx={n.x} cy={n.y} r={n.r}
                  fill={color} fillOpacity={0.75}
                  stroke={color} strokeWidth={0.8} strokeOpacity={0.4}
                />
                {n.r >= 5.5 && n.node.kpCount > 0 && (
                  <text
                    x={n.x} y={n.y + 0.5}
                    textAnchor="middle" dominantBaseline="middle"
                    fill="white" fillOpacity={0.9}
                    fontSize={Math.max(6, n.r * 0.8)} fontWeight={600}
                    style={{ pointerEvents: 'none' }}
                  >
                    {n.node.kpCount}
                  </text>
                )}
              </g>
            );
          })}

          {/* Stats */}
          <text x={dims.w / 2} y={dims.h - 8} textAnchor="middle" fill="#3d5060" fontSize={10}>
            {flatNodes.length} nodes ({parentCount} categories + {leafCount} leaves)
          </text>
        </svg>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="cefr-tree-tooltip"
          style={{
            left: Math.min(tooltip.x, dims.w - 300),
            top: Math.min(tooltip.y, dims.h - 120),
          }}
        >
          <div className="tooltip-title">{tooltip.node.labelZh || tooltip.node.label}</div>
          <div className="tooltip-subtitle">{tooltip.node.label}</div>
          <div className="tooltip-badges">
            <span className="tooltip-badge" style={{ background: `${CEFR_COLORS[tooltip.node.cefrMin]}20`, color: CEFR_COLORS[tooltip.node.cefrMin] }}>
              {tooltip.node.cefrMin === tooltip.node.cefrMax ? tooltip.node.cefrMin : `${tooltip.node.cefrMin}–${tooltip.node.cefrMax}`}
            </span>
            <span className="tooltip-badge" style={{ background: `${CATEGORY_COLORS[tooltip.node.category]}30`, color: CATEGORY_COLORS[tooltip.node.category] }}>
              {tooltip.node.category}
            </span>
            {tooltip.node.isParent && (
              <span className="tooltip-badge tooltip-badge-dim">
                {tooltip.node.childCount} children
              </span>
            )}
          </div>
          {tooltip.node.kpCount > 0 && (
            <div className="tooltip-stats">{tooltip.node.kpCount} knowledge points</div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="cefr-tree-legend">
        {(['grammar', 'vocabulary', 'usage'] as const).map(cat => (
          <div key={cat} className="legend-item">
            <span className="legend-dot" style={{ background: CATEGORY_COLORS[cat] }} />
            {cat === 'grammar' ? '文法' : cat === 'vocabulary' ? '詞彙' : '用法'}
          </div>
        ))}
        <div className="legend-sep" />
        <div className="legend-item legend-dim">
          <span className="legend-dot legend-dot-hollow" />
          分類
        </div>
        <div className="legend-item legend-dim">
          <span className="legend-dot legend-dot-filled" />
          葉節點
        </div>
      </div>
    </div>
  );
};

export default CEFRTreePage;
